import moment from 'moment';
import { ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '@/redux/scope';
import { FlowTask, flowTasksService } from "@/app/providers/services";
import styled from '@emotion/styled';
import { hexToHSL, system } from "@/theme";

import { EmptyContent } from '@/common';
import { useScrollable } from '@/hooks/use-scrollable';
import { priorityColor, statusColor } from '@/features/tasks/taskUtils';
import { Label } from '@/components';
import { AssigneeGroup } from "@/ui/forms/assignee-group";
import { Sort } from '@imminently/immi-query';
import { Tooltip, Typography } from '@mui/material';
import SplitPane from "@imminently/decisively-ui/SplitPane";

import { ShortID, StatusCircle } from './utils';

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  /* height: calc(100% - 4rem); */
  overflow-y: auto;

  #header {
    gap: 0.5rem;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1.5rem 1rem 1rem 1rem;

    border-bottom: 1px solid var(--border);

    .MuiTypography-root {
      font-size: 1.125rem; // 18px
      font-weight: 600;
    }
  }
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  height: 4rem;
  padding: 0 1rem;
  cursor: pointer;

  &[data-even="true"] {
    background-color: var(--background);
  }
  &[data-even="false"] {
    background-color: var(--subtle-light);
  }
  &:hover {
    background-color: var(--secondary-hover);
  }

  &[aria-selected="true"] {
    border: 1px solid var(--border);
    border-left: 4px solid var(--primary);
    background-color: var(--secondary-hover);
  }
`;

const SessionID = styled(ShortID)`
  color: var(--secondary-dark);
  text-transform: uppercase;
  font-weight: 600;
`;

type TaskListItemProps = {
  task: FlowTask;
  index: number;
  onClick: (task: FlowTask) => void;
  active?: boolean;
};

const TaskListItem = ({ task, index, active = false, onClick }: TaskListItemProps) => {
  const { t } = useTranslation();
  // date in format 24 June, 2024, using moment.js to format
  const dueDate = task.dueDate ? moment(task.dueDate).format("DD MMM, YYYY") : '';
  return (
    <ListItem data-even={index % 2 === 0} aria-selected={active} onClick={() => onClick(task)}>
      <Tooltip title={t(`tasks.status.${task.status}`)} placement="left">
        <StatusCircle color={hexToHSL(statusColor[task.status] ?? "#ff00ff", 80)} />
      </Tooltip>
      <div className="flex flex-col flex-1 min-w-0">
        <Tooltip title={task.title ?? task.name ?? ''} enterDelay={300}>
          <Typography noWrap>{task.title ?? task.name}</Typography>
        </Tooltip>
        <Typography variant="caption">Session: <SessionID value={task.session} prefix="#" variant="caption" /></Typography>
      </div>
      <AssigneeGroup users={task.assigneesData} emptyText={null} />
      <Typography variant="caption">{dueDate}</Typography>
      <div className="flex justify-end min-w-[4rem]">
        <Label color={hexToHSL(priorityColor[task.priority] ?? "#ff00ff", 90)}>
          {t(`tasks.priority.${task.priority}`)}
        </Label>
      </div>
    </ListItem>
  );
};

/** Hook for list of flow tasks, scoped to current workspace/project */
export const useFlowTasks = (filter: any = {}) => {
  const { workspace, project } = useScope();
  return flowTasksService.useInfiniteResources({
    perPage: 10,
    filter: {
      type: "flow",
      sort: { field: "created", order: "desc" } as Sort,
      status: ["notstarted", "inprogress", "not-started", "in-progress", "review", "blocked", "error"],
      workspace,
      model: project,
      ...filter,
    },
    refetchOnWindowFocus: false,
  });
};

export type TaskListProps = ComponentPropsWithoutRef<typeof ListContainer> & {
  tasks: FlowTask[];
  isLoading?: boolean;
  total?: number;
  loadMore?: () => void;
  canLoad?: boolean;
  onTaskClick: (task: FlowTask) => void;
  /** Active task id */
  activeId?: string;
};

// empty function that does nothing
const noop = () => { };

export const TaskList = ({ tasks, isLoading = false, total, loadMore = noop, canLoad = false, onTaskClick, activeId, ...props }: TaskListProps) => {
  const { t } = useTranslation();
  const scrollLoad = useScrollable(loadMore, canLoad, { autoScroll: true });

  return (
    <ListContainer ref={scrollLoad.ref} onScroll={scrollLoad.onScroll} onWheel={scrollLoad.onWheel} {...props}>
      <div id="header" className="flex justify-between items-center">
        <div className={"gap-2 flex items-center"}>
          <Typography>{t('tasks.tasks')}</Typography>
          <Label color={system.orangeFiesta}>{total ?? tasks.length} {t('tasks.tasks').toLocaleLowerCase()}</Label>
        </div>
        <SplitPane.CollapsiblePane.ToggleButton />
      </div>
      {
        isLoading
          ? <EmptyContent messages={[t('loading_with_dots')]} img={null} />
          : tasks.length === 0
            ? <EmptyContent messages={[t('tasks.no_tasks')]} />
            : (
              <div className="flex flex-col">
                {
                  tasks.length > 0
                    ? tasks.map((task, index) => (
                      <TaskListItem key={task.id} active={task.id === activeId} index={index} task={task as FlowTask} onClick={onTaskClick} />
                    ))
                    : null
                }
              </div>
            )
      }
    </ListContainer>
  );
};
